import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { crmService, Client } from '../services/crmService';
import { ListPageHeader } from '../components/crm/ListPageHeader';
import EmailComposeModal from '../components/EmailComposeModal';
import EnrollSequenceModal from '../components/sequences/EnrollSequenceModal';
import ContactEmailHistory from '../components/ContactEmailHistory';
import { OutboundEmailRow } from '../services/emailService';
import { clientStageBadgeClass } from '../utils/statusStyles';
import { ListToolbar } from '../components/crm/ListToolbar';
import { DataListTable, Column } from '../components/crm/DataListTable';
import { DncBadge, dncRowClassName } from '../components/crm/DncBadge';
import { CrmEmptyState } from '../components/crm/EmptyState';
import { RowActionsMenu } from '../components/crm/RowActionsMenu';
import { CrmGridSkeleton } from '../components/crm/CrmGridSkeleton';
import { ViewLayoutToggle } from '../components/crm/ViewLayoutToggle';
import CsvImportWizard from '../components/crm/CsvImportWizard';
import { formatPhoneDisplay } from '../utils/phoneFormat';
import LeadTemperatureBadge from '../components/dashboard/LeadTemperatureBadge';
import { getDisplayLeadScore, getDisplayLeadTemperature } from '../utils/leadScore';
import { LifecycleStatusBadge } from '../components/crm/LifecycleStatusBadge';
import { ProspectLifecycleStatus } from '../services/crmService';
import { getWebsiteDisplayText, getWebsiteLinkHref, sanitizeWebsiteUrlForSave } from '../utils/websiteUrl';
import ContactFormModal from '../components/crm/ContactFormModal';

const Prospects: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const prospectsLayout = searchParams.get('layout') === 'grid' ? 'grid' : 'list';
  const setProspectsLayout = (mode: 'list' | 'grid') => {
    setSearchParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        if (mode === 'grid') n.set('layout', 'grid');
        else n.delete('layout');
        return n;
      },
      { replace: true }
    );
  };
  const [prospects, setProspects] = useState<Client[]>([]);
  const [prospectCount, setProspectCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCsvImportWizard, setShowCsvImportWizard] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewPanel, setShowViewPanel] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [editScheduledEmail, setEditScheduledEmail] = useState<OutboundEmailRow | null>(null);
  const [emailContact, setEmailContact] = useState<Client | null>(null);
  const [enrollContact, setEnrollContact] = useState<Client | null>(null);
  const [emailRefreshKey, setEmailRefreshKey] = useState(0);
  const [viewingProspect, setViewingProspect] = useState<Client | null>(null);
  const [selectedProspect, setSelectedProspect] = useState<Client | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [lifecycleFilter, setLifecycleFilter] = useState<ProspectLifecycleStatus | 'all'>('active');
  const [ordering, setOrdering] = useState('-updated_at');
  const [page, setPage] = useState(1);
  const openedContactRef = useRef<string | null>(null);

  const loadProspects = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {
        stage: 'prospect',
        page: String(page),
        lifecycle_status: lifecycleFilter,
      };
      if (ordering === 'attention') {
        params.sort = 'attention';
      } else {
        params.ordering = ordering;
      }
      if (searchTerm) params.search = searchTerm;

      const response = await crmService.getClients(params);
      setProspects(response.results || []);
      setProspectCount(response.count ?? response.results?.length ?? 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load prospects');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, ordering, page, lifecycleFilter]);


  useEffect(() => {
    loadProspects();
  }, [loadProspects]);

  useEffect(() => {
    const sort = searchParams.get('sort');
    if (sort === 'attention') {
      setOrdering('attention');
      return;
    }
    setOrdering((prev) => (prev === 'attention' ? '-updated_at' : prev));
  }, [searchParams]);

  /** `/prospects?contact=<uuid>` opens the detail panel (e.g. from Dashboard). */
  useEffect(() => {
    const cid = searchParams.get('contact');
    if (!cid) {
      openedContactRef.current = null;
      return;
    }
    if (openedContactRef.current === cid) return;

    const strip = () =>
      setSearchParams(
        (prev) => {
          const n = new URLSearchParams(prev);
          n.delete('contact');
          return n;
        },
        { replace: true }
      );

    const fromList = prospects.find((p) => p.id === cid);
    if (fromList) {
      setViewingProspect(fromList);
      setShowViewPanel(true);
      openedContactRef.current = cid;
      strip();
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const c = await crmService.getClient(cid);
        if (cancelled || c.stage !== 'prospect') return;
        setViewingProspect(c);
        setShowViewPanel(true);
        openedContactRef.current = cid;
        strip();
      } catch {
        /* invalid */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchParams, prospects, setSearchParams]);

  const handleCreateProspect = async (prospectData: any) => {
    try {
      await crmService.createClient({ ...prospectData, stage: 'prospect' });
      await loadProspects();
      setShowAddModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create prospect');
    }
  };

  const handleUpdateProspect = async (prospectId: string, prospectData: any) => {
    try {
      await crmService.updateClient(prospectId, { ...prospectData, stage: 'prospect' });
      await loadProspects();
      setShowEditModal(false);
      setSelectedProspect(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update prospect');
    }
  };

  const handleDeleteProspect = async (prospectId: string) => {
    if (window.confirm('Are you sure you want to delete this prospect?')) {
      try {
        await crmService.deleteClient(prospectId);
        await loadProspects();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete prospect');
      }
    }
  };

  const handleConvertToClient = async (prospectId: string) => {
    try {
      await crmService.updateClient(prospectId, { stage: 'client' });
      await loadProspects();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to convert prospect');
    }
  };

  const matchesLifecycleFilter = useCallback(
    (status?: string | null) => lifecycleFilter === 'all' || (status || 'active') === lifecycleFilter,
    [lifecycleFilter]
  );

  const applyLifecycleStatus = async (prospect: Client, lifecycle_status: ProspectLifecycleStatus) => {
    try {
      const updated = await crmService.updateClient(prospect.id, { lifecycle_status });
      setProspects((prev) => {
        const next = prev.map((p) => (p.id === prospect.id ? updated : p));
        return next.filter((p) => matchesLifecycleFilter(p.lifecycle_status));
      });
      setProspectCount((prev) => (matchesLifecycleFilter(updated.lifecycle_status) ? prev : Math.max(0, prev - 1)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update lifecycle status');
    }
  };

  const sortOptions = useMemo(
    () => [
      { value: '-updated_at', label: 'Recently updated' },
      { value: 'attention', label: 'Priority (needs action)' },
      { value: 'updated_at', label: 'Oldest update' },
      { value: '-estimated_deal_value', label: 'Est. value (high)' },
      { value: 'estimated_deal_value', label: 'Est. value (low)' },
      { value: 'full_name', label: 'Name A–Z' },
      { value: '-full_name', label: 'Name Z–A' },
      { value: '-created_at', label: 'Newest' },
    ],
    []
  );

  const PAGE_SIZE = 20;
  const totalPages = Math.max(1, Math.ceil((prospectCount || 0) / PAGE_SIZE));

  const getStageIcon = (stage: string) => {
    switch (stage) {
      case 'lead':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        );
      case 'prospect':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return null;
    }
  };

  const prospectColumns: Column<Client>[] = [
    {
      id: 'name',
      header: 'Name',
      mobilePriority: 'primary',
      mobileLabel: 'Name',
      render: (p) => (
        <div>
          <div className="font-medium text-fg flex items-center gap-2 flex-wrap">
            <span>{p.full_name || p.display_name}</span>
            {p.do_not_contact ? <DncBadge /> : null}
          </div>
          <div className="text-xs text-fg-muted">{p.email}</div>
        </div>
      ),
    },
    {
      id: 'compliance',
      header: 'Reach',
      mobileLabel: 'Reach',
      className: 'w-[7rem]',
      cellClassName: 'align-top',
      render: (p) =>
        p.do_not_contact ? (
          <span className="text-xs font-medium text-amber-900 dark:text-amber-100">Suppressed</span>
        ) : (
          <span className="text-xs text-fg-muted">Contactable</span>
        ),
    },
    { id: 'company', header: 'Company', mobileLabel: 'Company', render: (p) => p.company_name || '—' },
    {
      id: 'website',
      header: 'Website',
      render: (p) => {
        const href = getWebsiteLinkHref(p.website_url);
        const label = getWebsiteDisplayText(p.website_url) || '—';
        if (!href) return <span className="text-fg-muted">{label}</span>;
        return (
          <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
            {label}
          </a>
        );
      },
    },
    {
      id: 'priority',
      header: (
        <span title="Same hot/warm/cold signal as leads — contact hot prospects first">Priority</span>
      ),
      render: (p) => <LeadTemperatureBadge temperature={getDisplayLeadTemperature(p)} />,
    },
    {
      id: 'score',
      header: <span title="0–100 from fit, source, and activity">Score</span>,
      className: 'w-16',
      render: (p) => (
        <span className="tabular-nums text-fg text-sm" title="0–100">
          {getDisplayLeadScore(p)}
        </span>
      ),
    },
    {
      id: 'next_fu',
      header: 'Next follow-up',
      render: (p) =>
        p.next_follow_up_at ? new Date(p.next_follow_up_at).toLocaleString() : '—',
    },
    {
      id: 'lifecycle',
      header: 'Status / Lifecycle',
      render: (p) => <LifecycleStatusBadge status={p.lifecycle_status} />,
    },
    {
      id: 'value',
      header: 'Est. value',
      render: (p) =>
        p.estimated_deal_value != null && p.estimated_deal_value !== ''
          ? `$${Number(p.estimated_deal_value).toLocaleString()}`
          : '—',
    },
    {
      id: 'assigned',
      header: 'Assigned',
      render: (p) => p.assigned_to_name || '—',
    },
    {
      id: 'updated',
      header: 'Last activity',
      render: (p) => new Date(p.updated_at).toLocaleString(),
    },
    {
      id: 'actions',
      header: '',
      className: 'w-24',
      cellClassName: 'text-right',
      render: (p) => (
        <RowActionsMenu
          items={[
            {
              id: 'view',
              label: 'View',
              onClick: () => {
                setViewingProspect(p);
                setShowViewPanel(true);
              },
            },
            {
              id: 'edit',
              label: 'Edit',
              onClick: () => {
                setSelectedProspect(p);
                setShowEditModal(true);
              },
            },
            {
              id: 'email',
              label: 'Send email',
              onClick: () => {
                setEmailContact(p);
                setShowEmailModal(true);
              },
            },
            { id: 'sequence', label: 'Start sequence', onClick: () => setEnrollContact(p) },
            { id: 'convert', label: 'Mark as client', onClick: () => handleConvertToClient(p.id) },
            { id: 'nurturing', label: 'Move to Nurturing', onClick: () => applyLifecycleStatus(p, 'nurturing') },
            { id: 'dormant', label: 'Move to Dormant', onClick: () => applyLifecycleStatus(p, 'dormant') },
            { id: 'lost', label: 'Mark Lost', onClick: () => applyLifecycleStatus(p, 'lost') },
            { id: 'restore', label: 'Restore to Active', onClick: () => applyLifecycleStatus(p, 'active') },
            { id: 'del', label: 'Delete', onClick: () => handleDeleteProspect(p.id) },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <ListPageHeader
        title="Prospects"
        subtitle="Qualified opportunities — use priority and score to decide who gets your next call."
        actions={
          <>
            <button
              type="button"
              onClick={() => setShowCsvImportWizard(true)}
              className="btn-secondary px-4 py-2 rounded-xl text-sm font-medium"
            >
              Import CSV
            </button>
            <button
              type="button"
              onClick={() => setShowAddModal(true)}
              className="btn-primary px-4 py-2 rounded-xl text-sm font-medium"
            >
              Add prospect
            </button>
            <Link to="/leads" className="btn-secondary px-4 py-2 rounded-xl text-sm font-medium">
              Back to Leads
            </Link>
          </>
        }
      />

      <div className="flex flex-col gap-2">
        <ListToolbar
          searchValue={searchTerm}
          onSearchChange={(v) => {
            setSearchTerm(v);
            setPage(1);
          }}
          searchPlaceholder="Search name, company, email…"
          sortValue={ordering}
          sortOptions={sortOptions}
          onSortChange={(v) => {
            setOrdering(v);
            setPage(1);
            setSearchParams(
              (prev) => {
                const n = new URLSearchParams(prev);
                if (v === 'attention') n.set('sort', 'attention');
                else n.delete('sort');
                return n;
              },
              { replace: true }
            );
          }}
          showClear={!!(searchTerm || lifecycleFilter !== 'active')}
          onClearFilters={() => {
            setSearchTerm('');
            setOrdering('-updated_at');
            setLifecycleFilter('active');
            setPage(1);
            setSearchParams(
              (prev) => {
                const n = new URLSearchParams(prev);
                n.delete('sort');
                return n;
              },
              { replace: true }
            );
          }}
          advancedFilterCount={lifecycleFilter !== 'active' ? 1 : 0}
          advancedFilters={
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-fg-muted">Status / Lifecycle</label>
              <select
                value={lifecycleFilter}
                onChange={(e) => {
                  setLifecycleFilter(e.target.value as ProspectLifecycleStatus | 'all');
                  setPage(1);
                }}
                className="input-field rounded-xl border border-border bg-input px-3 py-2 text-sm text-fg min-w-[12rem]"
              >
                <option value="active">Active</option>
                <option value="nurturing">Nurturing</option>
                <option value="dormant">Dormant</option>
                <option value="lost">Lost</option>
                <option value="all">All</option>
              </select>
            </div>
          }
        />
        <div className="flex justify-end">
          <ViewLayoutToggle mode={prospectsLayout} onChange={setProspectsLayout} />
        </div>
      </div>

      {error && <div className="alert-danger rounded-xl px-4 py-3 text-sm">{error}</div>}

      {prospectsLayout === 'list' ? (
        <DataListTable<Client>
          columns={prospectColumns}
          rows={prospects}
          getRowKey={(p) => p.id}
          onRowClick={(p) => {
            setViewingProspect(p);
            setShowViewPanel(true);
          }}
          getRowClassName={(p) => dncRowClassName(p.do_not_contact)}
          loading={loading}
          skeletonColumns={12}
          emptyContent={
            <CrmEmptyState
              title="No prospects yet"
              description={
                lifecycleFilter === 'active'
                  ? 'Prospects are qualified deals you’re actively working—usually converted from a lead after you’ve confirmed fit.'
                  : 'No prospects match the selected lifecycle filter.'
              }
              actionLabel="Add a prospect"
              onAction={() => setShowAddModal(true)}
              secondaryHint="Start from Leads: qualify a contact, then use “Convert to prospect” on the lead row or profile."
            />
          }
        />
      ) : loading ? (
        <CrmGridSkeleton />
      ) : prospects.length === 0 ? (
        <CrmEmptyState
          title="No prospects yet"
          description={
            lifecycleFilter === 'active'
              ? 'Prospects are qualified deals you’re actively working—usually converted from a lead after you’ve confirmed fit.'
              : 'No prospects match the selected lifecycle filter.'
          }
          actionLabel="Add a prospect"
          onAction={() => setShowAddModal(true)}
          secondaryHint="Start from Leads: qualify a contact, then use “Convert to prospect” on the lead row or profile."
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {prospects.map((p) => (
            <div
              key={p.id}
              className="rounded-xl border border-border bg-surface p-4 shadow-sm transition hover:border-primary/40 hover:shadow-md"
              onClick={(e) => {
                if ((e.target as HTMLElement).closest('[data-row-action]')) return;
                setViewingProspect(p);
                setShowViewPanel(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  if ((e.target as HTMLElement).closest('[data-row-action]')) return;
                  e.preventDefault();
                  setViewingProspect(p);
                  setShowViewPanel(true);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-fg">{p.full_name || p.display_name}</div>
                  <div className="truncate text-xs text-fg-muted">{p.email}</div>
                </div>
                <div data-row-action className="shrink-0">
                  <RowActionsMenu
                    items={[
                      {
                        id: 'view',
                        label: 'View',
                        onClick: () => {
                          setViewingProspect(p);
                          setShowViewPanel(true);
                        },
                      },
                      {
                        id: 'edit',
                        label: 'Edit',
                        onClick: () => {
                          setSelectedProspect(p);
                          setShowEditModal(true);
                        },
                      },
                      {
                        id: 'email',
                        label: 'Send email',
                        onClick: () => {
                          setEmailContact(p);
                          setShowEmailModal(true);
                        },
                      },
                      { id: 'sequence', label: 'Start sequence', onClick: () => setEnrollContact(p) },
                      { id: 'convert', label: 'Mark as client', onClick: () => handleConvertToClient(p.id) },
                      { id: 'nurturing', label: 'Move to Nurturing', onClick: () => applyLifecycleStatus(p, 'nurturing') },
                      { id: 'dormant', label: 'Move to Dormant', onClick: () => applyLifecycleStatus(p, 'dormant') },
                      { id: 'lost', label: 'Mark Lost', onClick: () => applyLifecycleStatus(p, 'lost') },
                      { id: 'restore', label: 'Restore to Active', onClick: () => applyLifecycleStatus(p, 'active') },
                      { id: 'del', label: 'Delete', onClick: () => handleDeleteProspect(p.id) },
                    ]}
                  />
                </div>
              </div>
              <dl className="mt-3 grid grid-cols-2 gap-x-2 gap-y-1 text-xs">
                <dt className="text-fg-muted">Company</dt>
                <dd className="text-fg">{p.company_name || '—'}</dd>
                <dt className="text-fg-muted">Website</dt>
                <dd className="text-fg">
                  {(() => {
                    const href = getWebsiteLinkHref(p.website_url);
                    const label = getWebsiteDisplayText(p.website_url) || '—';
                    if (!href) return <span className="text-fg-muted">{label}</span>;
                    return (
                      <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        {label}
                      </a>
                    );
                  })()}
                </dd>
                <dt className="text-fg-muted">Lifecycle</dt>
                <dd className="text-fg">
                  <LifecycleStatusBadge status={p.lifecycle_status} />
                </dd>
                <dt className="text-fg-muted">Est. value</dt>
                <dd className="text-fg">
                  {p.estimated_deal_value != null && p.estimated_deal_value !== ''
                    ? `$${Number(p.estimated_deal_value).toLocaleString()}`
                    : '—'}
                </dd>
                <dt className="text-fg-muted">Assigned</dt>
                <dd className="text-fg">{p.assigned_to_name || '—'}</dd>
                <dt className="text-fg-muted">Last activity</dt>
                <dd className="text-fg">{new Date(p.updated_at).toLocaleString()}</dd>
              </dl>
            </div>
          ))}
        </div>
      )}
      {totalPages > 1 ? (
        <div className="flex items-center justify-center gap-4 text-sm text-fg-muted">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="btn-surface-muted rounded-lg px-3 py-1 disabled:opacity-50"
          >
            Previous
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="btn-surface-muted rounded-lg px-3 py-1 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      ) : null}

      {/* Add Prospect Modal */}
      {showAddModal && (
        <AddProspectModal
          onClose={() => setShowAddModal(false)}
          onSuccess={handleCreateProspect}
        />
      )}

      {showCsvImportWizard && (
        <CsvImportWizard
          open={showCsvImportWizard}
          onClose={() => setShowCsvImportWizard(false)}
          defaultObjectType="prospect"
          lockObjectType
          onImported={() => loadProspects()}
        />
      )}

      {/* Edit Prospect Modal */}
      {showEditModal && selectedProspect && (
        <EditProspectModal
          prospect={selectedProspect}
          onSave={(prospectData) => handleUpdateProspect(selectedProspect.id, prospectData)}
          onClose={() => {
            setShowEditModal(false);
            setSelectedProspect(null);
          }}
        />
      )}

      {/* View Prospect Panel (read-only) */}
      {showViewPanel && viewingProspect && (
        <ProspectViewPanel
          prospect={viewingProspect}
          onClose={() => {
            setShowViewPanel(false);
            setViewingProspect(null);
            setShowEmailModal(false);
          }}
          onEdit={() => {
            setSelectedProspect(viewingProspect);
            setShowViewPanel(false);
            setShowEditModal(true);
            setViewingProspect(null);
            setShowEmailModal(false);
          }}
          onConvertToClient={() => handleConvertToClient(viewingProspect.id)}
          getStageIcon={getStageIcon}
          onOpenEmail={() => {
            setEmailContact(viewingProspect);
            setEditScheduledEmail(null);
            setShowEmailModal(true);
          }}
          onEditScheduled={(row) => {
            setEmailContact(viewingProspect);
            setEditScheduledEmail(row);
            setShowEmailModal(true);
          }}
          emailRefreshKey={emailRefreshKey}
        />
      )}

      {showEmailModal && emailContact && (
        <EmailComposeModal
          open={showEmailModal}
          onClose={() => {
            setShowEmailModal(false);
            setEditScheduledEmail(null);
            setEmailContact(null);
          }}
          contact={emailContact}
          contactKind="client"
          followUpContext="prospect"
          editScheduledEmail={editScheduledEmail}
          onSent={() => setEmailRefreshKey((k) => k + 1)}
        />
      )}

      {enrollContact && (
        <EnrollSequenceModal
          contact={{
            type: 'client',
            id: enrollContact.id,
            name: enrollContact.full_name || enrollContact.display_name || enrollContact.email,
          }}
          onClose={() => setEnrollContact(null)}
        />
      )}
    </div>
  );
};

interface AddProspectModalProps {
  onClose: () => void;
  onSuccess: (prospectData: any) => void;
}

const AddProspectModal: React.FC<AddProspectModalProps> = ({ onClose, onSuccess }) => (
  <ContactFormModal
    title="Add prospect"
    submitLabel="Create prospect"
    mode="prospect"
    values={{
      full_name: '',
      email: '',
      phone: '',
      website_url: '',
      stage: 'prospect',
      source: 'Direct',
      company_name: '',
      job_title: '',
      tags: [],
      address: '',
      notes: '',
      lifecycle_status: 'active' as ProspectLifecycleStatus,
    }}
    onClose={onClose}
    onSubmit={(payload) => onSuccess({ ...payload, website_url: sanitizeWebsiteUrlForSave(payload.website_url) })}
  />
);

interface EditProspectModalProps {
  prospect: Client;
  onSave: (prospectData: any) => void;
  onClose: () => void;
}

const EditProspectModal: React.FC<EditProspectModalProps> = ({ prospect, onSave, onClose }) => (
  <ContactFormModal
    title="Edit prospect"
    submitLabel="Save changes"
    mode="prospect"
    values={{
      title: prospect.title || '',
      full_name: prospect.full_name || '',
      email: prospect.email || '',
      phone: prospect.phone || '',
      website_url: prospect.website_url || '',
      stage: (prospect.stage as 'prospect' | 'client') || 'prospect',
      source: prospect.source || 'Direct',
      company_name: prospect.company_name || '',
      job_title: prospect.job_title || '',
      tags: prospect.tags || [],
      address: prospect.address || '',
      notes: prospect.notes || '',
      lifecycle_status: (prospect.lifecycle_status as ProspectLifecycleStatus) || 'active',
    }}
    onClose={onClose}
    onSubmit={(payload) => onSave({ ...payload, website_url: sanitizeWebsiteUrlForSave(payload.website_url) })}
  />
);

// View Prospect Panel (read-only)
interface ProspectViewPanelProps {
  prospect: Client;
  onClose: () => void;
  onEdit: () => void;
  onConvertToClient: () => void;
  getStageIcon: (stage: string) => React.ReactNode;
  onOpenEmail: () => void;
  onEditScheduled: (row: OutboundEmailRow) => void;
  emailRefreshKey: number;
}

const ProspectViewPanel: React.FC<ProspectViewPanelProps> = ({ prospect, onClose, onEdit, onConvertToClient, getStageIcon, onOpenEmail, onEditScheduled, emailRefreshKey }) => {
  return (
    <div className="modal-backdrop">
      <div className="relative top-10 mx-auto p-5 border border-border w-full max-w-2xl shadow-lg rounded-2xl bg-surface">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-fg">Prospect Details</h3>
            <button
              onClick={onClose}
              className="text-fg-muted hover:text-fg-secondary"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-6">
            <div className="flex items-center">
              <div className="w-16 h-16 bg-gradient-to-r from-yellow-500 to-orange-600 rounded-full flex items-center justify-center">
                <span className="text-white font-semibold text-xl">
                  {prospect.full_name?.[0] || prospect.email?.[0] || '?'}
                </span>
              </div>
              <div className="ml-4">
                <h2 className="text-xl font-bold text-fg">{prospect.full_name || prospect.display_name}</h2>
                <p className="text-fg-muted">{prospect.email}</p>
                <span className={`inline-flex items-center mt-2 ${clientStageBadgeClass(prospect.stage)}`}>
                  {getStageIcon(prospect.stage)}
                  <span className="ml-1">{prospect.stage}</span>
                </span>
              </div>
            </div>

            <div className="rounded-xl border border-border bg-surface-muted/20 p-4">
              <h4 className="text-sm font-semibold text-fg mb-3">Business Identity</h4>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-fg-secondary">Phone</label>
                <p className="text-fg">{formatPhoneDisplay(prospect.phone)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-fg-secondary">Company</label>
                <p className="text-fg">{prospect.company_name || '—'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-fg-secondary">Website</label>
                {(() => {
                  const href = getWebsiteLinkHref(prospect.website_url);
                  const label = getWebsiteDisplayText(prospect.website_url) || '—';
                  if (!href) return <p className="text-fg-muted">{label}</p>;
                  return (
                    <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      {label}
                    </a>
                  );
                })()}
              </div>
              <div>
                <label className="block text-sm font-medium text-fg-secondary">Job Title</label>
                <p className="text-fg">{prospect.job_title || '—'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-fg-secondary">Source</label>
                <p className="text-fg">{prospect.source || '—'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-fg-secondary">Added</label>
                <p className="text-fg">{new Date(prospect.created_at).toLocaleDateString()}</p>
              </div>
            </div>
            </div>

            {prospect.address && (
              <div>
                <label className="block text-sm font-medium text-fg-secondary mb-1">Address</label>
                <p className="text-fg whitespace-pre-wrap">{prospect.address}</p>
              </div>
            )}

            {prospect.notes && (
              <div>
                <label className="block text-sm font-medium text-fg-secondary mb-1">Notes</label>
                <p className="text-fg whitespace-pre-wrap">{prospect.notes}</p>
              </div>
            )}

            <div className="border-t border-border pt-4">
              <h4 className="text-sm font-semibold text-fg mb-2">Email</h4>
              <ContactEmailHistory clientId={prospect.id} refreshKey={emailRefreshKey} onEditScheduled={onEditScheduled} />
              <button
                type="button"
                onClick={onOpenEmail}
                className="mt-3 px-4 py-2 text-sm font-medium text-primary-fg bg-primary hover:bg-primary-dark rounded-xl"
              >
                Send follow-up
              </button>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-border">
              <button
                onClick={onClose}
                className="px-4 py-2 btn-surface-muted rounded-xl"
              >
                Close
              </button>
              <button
                onClick={onEdit}
                className="px-4 py-2 text-sm font-medium text-primary-fg bg-primary hover:bg-primary-dark rounded-xl"
              >
                Edit
              </button>
              <button
                onClick={onConvertToClient}
                className="px-4 py-2 text-sm font-medium text-primary-fg bg-gradient-to-r from-primary to-primary-dark hover:from-primary-dark hover:to-primary rounded-xl"
              >
                Convert to Client
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Prospects;
